import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Button, PageHeader } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { PermissionGate } from "@/components/PermissionGate";
import {
  useArchiveContentTemplate,
  useContentTemplate,
  useContentTemplates,
  useCreateContentTemplate,
  type ContentTemplateFilters,
} from "@/hooks/useContentTemplates";
import type {
  AudienceRole,
  ContentType,
  Locale,
  TemplateStatus,
  TemplateSummary,
} from "@/types/content";

const CONTENT_TYPE_OPTIONS: { value: ContentType | "all"; label: string }[] = [
  { value: "all", label: "All types" },
  { value: "product_caption", label: "Product caption" },
  { value: "motivational_nudge", label: "Motivational nudge" },
  { value: "voice_message", label: "Voice message" },
  { value: "gamified_prompt", label: "Gamified prompt" },
];

const LOCALE_OPTIONS: { value: Locale | "all"; label: string }[] = [
  { value: "all", label: "All locales" },
  { value: "ph", label: "Philippines (ph)" },
  { value: "id", label: "Indonesia (id)" },
  { value: "uae", label: "UAE (uae)" },
  { value: "usa", label: "USA (usa)" },
  { value: "hk", label: "Hong Kong (hk)" },
  { value: "cy", label: "Cyprus (cy)" },
  { value: "global", label: "Global (global)" },
];

const AUDIENCE_OPTIONS: { value: AudienceRole | "all"; label: string }[] = [
  { value: "all", label: "All audiences" },
  { value: "guest", label: "Guest" },
  { value: "business_owner", label: "Business Owner" },
  { value: "preferred_member", label: "Preferred Member" },
  { value: "product_center", label: "Product Center" },
  { value: "branch_operator", label: "Branch Operator" },
  { value: "admin", label: "Admin" },
];

const STATUS_OPTIONS: { value: TemplateStatus | "all"; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  product_caption: "Product caption",
  motivational_nudge: "Motivational nudge",
  voice_message: "Voice message",
  gamified_prompt: "Gamified prompt",
};

const AUDIENCE_LABELS: Record<AudienceRole, string> = {
  guest: "Guest",
  business_owner: "Business Owner",
  preferred_member: "Preferred Member",
  product_center: "Product Center",
  branch_operator: "Branch Operator",
  admin: "Admin",
};

const AUDIENCE_RB: Record<AudienceRole, string> = {
  guest: "guest",
  business_owner: "bo",
  preferred_member: "pm",
  product_center: "pc",
  branch_operator: "bop",
  admin: "admin",
};

const statusBadge = (status: TemplateStatus) => {
  if (status === "active")
    return (
      <span className="b suc">
        <span className="dot" />
        {status}
      </span>
    );
  if (status === "draft") return <span className="b war">{status}</span>;
  return <span className="b def">{status}</span>;
};

interface FilterState {
  content_type: ContentType | "all";
  locale: Locale | "all";
  audience_role: AudienceRole | "all";
  status: TemplateStatus | "all";
}

const DEFAULT_FILTERS: FilterState = {
  content_type: "all",
  locale: "all",
  audience_role: "all",
  status: "active",
};

const buildFilterPayload = (state: FilterState): ContentTemplateFilters => {
  const payload: ContentTemplateFilters = {};
  if (state.content_type !== "all") payload.content_type = state.content_type;
  if (state.locale !== "all") payload.locale = state.locale;
  if (state.audience_role !== "all") payload.audience_role = state.audience_role;
  if (state.status !== "all") payload.status = state.status;
  return payload;
};

interface DuplicateActionProps {
  template: TemplateSummary;
}

// Duplication needs the full template body (system_prompt, input_schema, etc.),
// not just the row summary — so we lazily load detail when the user clicks.
const DuplicateAction = ({ template }: DuplicateActionProps) => {
  const [requested, setRequested] = useState(false);
  const { data: detail, isFetching } = useContentTemplate(
    requested ? template.id : ("new" as const)
  );
  const createMutation = useCreateContentTemplate();

  const handleClick = async () => {
    if (!requested) {
      setRequested(true);
      return;
    }
    if (!detail) return;
    try {
      await createMutation.mutateAsync({
        slug: `${detail.slug}-copy`,
        content_type: detail.content_type,
        locale: detail.locale,
        audience_role: detail.audience_role,
        title: `${detail.title} (copy)`,
        description: detail.description ?? null,
        system_prompt: detail.system_prompt,
        input_schema: detail.input_schema,
        model_overrides: detail.model_overrides ?? null,
        use_rag: detail.use_rag,
        rag_context: detail.rag_context ?? null,
        tts_voice: detail.tts_voice ?? null,
        status: "draft",
      });
    } finally {
      setRequested(false);
    }
  };

  const loading = (requested && isFetching) || createMutation.isPending;

  return (
    <button
      type="button"
      className="act"
      onClick={handleClick}
      disabled={loading}
      title="Duplicate"
    >
      <Icon name={loading ? "refresh" : "copy"} />
    </button>
  );
};

const ContentTemplates = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);

  const filterPayload = useMemo(() => buildFilterPayload(filters), [filters]);
  const { data, isLoading } = useContentTemplates(filterPayload);
  const archiveMutation = useArchiveContentTemplate();

  const items = useMemo(() => {
    const list = data?.items ? [...data.items] : [];
    list.sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
    return list;
  }, [data]);

  const handleReset = () => setFilters(DEFAULT_FILTERS);

  const handleArchive = (template: TemplateSummary) => {
    const ok = window.confirm(
      `Archive "${template.title}"? Active callers will fall back to the next active template for (${template.content_type}, ${template.locale}, ${template.audience_role}).`
    );
    if (!ok) return;
    archiveMutation.mutate(template.id);
  };

  const hasResults = items.length > 0;

  return (
    <PermissionGate
      resource="content_templates"
      action="view"
      fallback={
        <div className="page wide">
          <PageHeader
            label="Content Generation"
            title="Content Templates"
            description="Prompt templates for content generation, per content type × locale × audience role."
          />
          <div className="card">
            <div className="card-b">
              <p className="meta" style={{ padding: "24px 0", textAlign: "center" }}>
                You do not have permission to view content templates.
              </p>
            </div>
          </div>
        </div>
      }
    >
      <div className="page wide">
        <PageHeader
          label="Content Generation"
          title="Content Templates"
          description="Prompt templates for content generation, per content type × locale × audience role."
          actions={
            <PermissionGate resource="content_templates" action="create">
              <Button onClick={() => navigate("/content-templates/new")}>
                <Icon name="plus" />
                New template
              </Button>
            </PermissionGate>
          }
        />

        <div className="card">
          <div className="card-b">
            <div className="row c4">
              <div className="fg">
                <label className="fl">Content type</label>
                <select
                  className="sel"
                  value={filters.content_type}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      content_type: e.target
                        .value as FilterState["content_type"],
                    }))
                  }
                >
                  {CONTENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg">
                <label className="fl">Locale</label>
                <select
                  className="sel"
                  value={filters.locale}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      locale: e.target.value as FilterState["locale"],
                    }))
                  }
                >
                  {LOCALE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg">
                <label className="fl">Audience role</label>
                <select
                  className="sel"
                  value={filters.audience_role}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      audience_role: e.target
                        .value as FilterState["audience_role"],
                    }))
                  }
                >
                  {AUDIENCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg">
                <label className="fl">Status</label>
                <select
                  className="sel"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: e.target.value as FilterState["status"],
                    }))
                  }
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
              <Button type="button" variant="ghost" size="sm" onClick={handleReset}>
                <Icon name="refresh" />
                Reset filters
              </Button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-h tight">
            <div>
              <h2>Templates</h2>
              <p>
                {isLoading
                  ? "Loading…"
                  : `${items.length} of ${data?.total ?? items.length} shown, sorted by most recently updated`}
              </p>
            </div>
          </div>

          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th style={{ width: "26%" }}>Title</th>
                  <th>Type</th>
                  <th style={{ width: 70 }}>Locale</th>
                  <th>Audience</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 130 }}>
                    Updated <span className="ar">↓</span>
                  </th>
                  <th style={{ width: 110, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((tpl) => (
                  <tr key={tpl.id}>
                    <td>
                      <div className="pri-cell">{tpl.title}</div>
                      <div className="sub-cell">{tpl.slug}</div>
                    </td>
                    <td>{CONTENT_TYPE_LABELS[tpl.content_type]}</td>
                    <td
                      className="mono"
                      style={{ textTransform: "uppercase", fontWeight: 600 }}
                    >
                      {tpl.locale}
                    </td>
                    <td>
                      <span className={`rb ${AUDIENCE_RB[tpl.audience_role]}`}>
                        {AUDIENCE_LABELS[tpl.audience_role]}
                      </span>
                    </td>
                    <td>{statusBadge(tpl.status)}</td>
                    <td>
                      <div className="num">
                        {format(new Date(tpl.updated_at), "MMM d, yyyy")}
                      </div>
                      <div
                        className="sub-cell"
                        style={{ fontFamily: "var(--sans)" }}
                      >
                        {tpl.updated_by}
                      </div>
                    </td>
                    <td>
                      <div className="acts">
                        <PermissionGate
                          resource="content_templates"
                          action="edit"
                        >
                          <Link
                            to={`/content-templates/${tpl.id}`}
                            className="act"
                            title="Edit"
                          >
                            <Icon name="edit" />
                          </Link>
                        </PermissionGate>
                        <PermissionGate
                          resource="content_templates"
                          action="create"
                        >
                          <DuplicateAction template={tpl} />
                        </PermissionGate>
                        <PermissionGate
                          resource="content_templates"
                          action="delete"
                        >
                          <button
                            type="button"
                            className="act d"
                            onClick={() => handleArchive(tpl)}
                            disabled={
                              archiveMutation.isPending &&
                              archiveMutation.variables === tpl.id
                            }
                            title="Archive"
                          >
                            <Icon name="arch" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}

                {!isLoading && !hasResults && (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty sm">
                        <b>No templates match these filters.</b>
                        <button
                          type="button"
                          onClick={handleReset}
                          className="btn gho sm"
                          style={{ marginTop: 10 }}
                        >
                          Clear filters
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PermissionGate>
  );
};

export default ContentTemplates;
